/**
 * 픽셀 캔버스 컴포넌트
 * 손글씨 입력용 HTML5 Canvas
 */

import React, { useRef, useEffect, useState } from 'react';

/**
 * PixelCanvas Props
 */
interface PixelCanvasProps {
  /** 캔버스 너비 */
  width?: number;
  /** 캔버스 높이 */
  height?: number;
  /** 펜 색상 */
  penColor?: string;
  /** 펜 굵기 */
  penWidth?: number;
  /** 지우개 모드 */
  isEraser?: boolean;
  /** 반응형 크기 활성화 */
  responsive?: boolean;
  /** 점 보간 활성화 (빠른 입력 개선) */
  smoothing?: boolean;
}

/**
 * PixelCanvas 컴포넌트
 *
 * - HTML5 Canvas 기반 드로잉
 * - 마우스/터치 지원
 * - 부드러운 선 그리기
 */
export const PixelCanvas = React.forwardRef<HTMLCanvasElement, PixelCanvasProps>(
  ({
    width = 350,
    height = 200,
    penColor = '#000000',
    penWidth = 3,
    isEraser = false,
    responsive = false,
    smoothing = true
  }, ref) => {
    const internalRef = useRef<HTMLCanvasElement>(null);
    const canvasRef = (ref as React.RefObject<HTMLCanvasElement>) || internalRef;
    const [isDrawing, setIsDrawing] = useState(false);
    const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
    const [canvasSize, setCanvasSize] = useState({ width, height });
    const lastPos = useRef({ x: 0, y: 0 });

    /**
     * 반응형 크기 계산
     */
    useEffect(() => {
      if (!responsive) {
        setCanvasSize({ width, height });
        return;
      }

      const updateSize = () => {
        const screenWidth = window.innerWidth;
        if (screenWidth >= 1024) {
          // 데스크톱
          setCanvasSize({ width: 700, height: 400 });
        } else if (screenWidth >= 768) {
          // 태블릿
          setCanvasSize({ width: 500, height: 300 });
        } else {
          // 모바일
          setCanvasSize({ width: 350, height: 200 });
        }
      };

      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }, [responsive, width, height]);

    /**
     * Canvas 초기화
     */
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // 캔버스 배경 흰색으로 초기화
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

      setContext(ctx);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvasSize.width, canvasSize.height]);

    /**
     * Passive: false로 터치 이벤트 직접 등록
     * React 이벤트는 passive 모드라 preventDefault()가 무시될 수 있음
     */
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const handleTouchStartCapture = (e: TouchEvent) => {
        e.preventDefault(); // 터치 선택/스크롤 완전 차단
      };

      const handleTouchMoveCapture = (e: TouchEvent) => {
        e.preventDefault(); // 터치 스크롤 완전 차단
      };

      // passive: false로 등록하여 preventDefault() 활성화
      canvas.addEventListener('touchstart', handleTouchStartCapture, { passive: false });
      canvas.addEventListener('touchmove', handleTouchMoveCapture, { passive: false });

      return () => {
        canvas.removeEventListener('touchstart', handleTouchStartCapture);
        canvas.removeEventListener('touchmove', handleTouchMoveCapture);
      };
    }, []);

    /**
     * 그리기 시작
     */
    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!context) return;

      setIsDrawing(true);

      const pos = getPosition(e);
      lastPos.current = pos;
      context.beginPath();
      context.moveTo(pos.x, pos.y);
    };

    /**
     * 점 보간 함수 (빠른 입력 시 선 끊김 방지)
     */
    const interpolatePoints = (x0: number, y0: number, x1: number, y1: number) => {
      if (!context) return;

      const distance = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
      const steps = Math.ceil(distance / 2); // 2px마다 점 추가

      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const x = x0 + (x1 - x0) * t;
        const y = y0 + (y1 - y0) * t;
        context.lineTo(x, y);
      }
    };

    /**
     * 그리기 중
     */
    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing || !context) return;

      const pos = getPosition(e);

      // 펜/지우개 설정
      if (isEraser) {
        context.globalCompositeOperation = 'destination-out';
        context.lineWidth = penWidth * 3;
      } else {
        context.globalCompositeOperation = 'source-over';
        context.lineWidth = penWidth;
        context.strokeStyle = penColor;
      }

      context.lineCap = 'round';
      context.lineJoin = 'round';

      // 점 보간 사용 여부
      if (smoothing) {
        interpolatePoints(lastPos.current.x, lastPos.current.y, pos.x, pos.y);
      } else {
        context.lineTo(pos.x, pos.y);
      }

      context.stroke();
      lastPos.current = pos;
    };

    /**
     * 그리기 종료
     */
    const stopDrawing = () => {
      setIsDrawing(false);
    };

    /**
     * 마우스/터치 위치 계산
     */
    const getPosition = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();

      if ('touches' in e) {
        const touch = e.touches[0];
        return {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        };
      } else {
        return {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
      }
    };

    /**
     * 터치 이벤트 핸들러 (선택/스크롤 방지)
     */
    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault(); // 터치 선택 방지
      startDrawing(e);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault(); // 터치 스크롤 방지
      draw(e);
    };

    return (
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={stopDrawing}
        className="border-2 border-gray-700 rounded-sm cursor-crosshair bg-white
                   touch-none select-none"
        style={{
          imageRendering: 'pixelated',
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      />
    );
  }
);

/**
 * Canvas를 Blob으로 변환하는 유틸리티 함수
 */
export const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas to Blob 변환 실패'));
        }
      },
      'image/png',
      1.0
    );
  });
};

/**
 * Canvas 초기화 함수
 */
export const clearCanvas = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
};

PixelCanvas.displayName = 'PixelCanvas';
